import { Edge, FamilyTreeNode } from '@/components/diagrams/FlowChart';

import { findNodesReachableFromPrimaryClients } from './findNodesReachableFromPrimaryClients';

type PersonData = {
  id: string;
  isPrimary: boolean;
  generation?: number;
  [key: string]: unknown;
};

/**
 * Filters out orphaned nodes that are not connected to primary clients.
 * Uses BFS to find all nodes reachable from primary clients via edges.
 *
 * @param nodes - All nodes in the family tree
 * @param edges - All edges connecting the nodes
 * @param persons - Person data including primary and generation flags
 * @returns Filtered nodes and edges containing only those connected to primary clients
 */
export function filterOrphanedNodes(
  nodes: FamilyTreeNode[],
  edges: Edge[],
  persons: PersonData[]
): { filteredNodes: FamilyTreeNode[]; filteredEdges: Edge[] } {
  const reachableNodeIds = findNodesReachableFromPrimaryClients(
    nodes,
    edges,
    persons
  );

  // If no nodes are reachable, return all nodes (edge case - no primary clients)
  if (reachableNodeIds.size === 0) {
    return { filteredNodes: nodes, filteredEdges: edges };
  }

  const standalonePersonIds = new Set(
    persons
      .filter((person) => person.generation === -99)
      .map((person) => person.id)
  );

  // Filter nodes and edges to only include reachable ones
  const filteredNodes = nodes.filter((node) => {
    if (reachableNodeIds.has(node.id)) return true;
    if (node.type !== 'person') return false;
    return standalonePersonIds.has(node.data.person.id);
  });
  const filteredEdges = edges.filter(
    (edge) =>
      reachableNodeIds.has(edge.source) && reachableNodeIds.has(edge.target)
  );

  return { filteredNodes, filteredEdges };
}
